import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import echarts from '@/utils/echartsCore';
import {DataLabelPosition} from 'entities/chart';
import {buildChartImageDownloadToolboxFeatures} from '@/views/storyboard/utils/chartImageDownload';
import {SunburstTreeNode} from '../utils/aggregationsToSunburstTree';
import {formatHierarchyChartTooltip, HierarchyTooltipParams} from '../utils/hierarchyChartTooltip';
import {
    buildTreemapLevels,
    treemapTileLabelFormatter,
    treemapUpperLabelFormatter,
    TREEMAP_TILE_ITEM_STYLE,
    TREEMAP_UPPER_LABEL_BACKGROUND,
} from '../utils/treemapChartLayout';

export interface TreemapChartProps {
    data: SunburstTreeNode[];
    levelCount?: number;
    height?: number;
    width?: number;
    chartSettingsView?: boolean;
    title?: string;
    dataLabelPosition?: DataLabelPosition;
    description?: string;
    datePeriod?: string;
}

const TREEMAP_CHART_PADDING = {
    horizontal: 24,
    vertical: 8,
    toolboxInset: 10,
    toolboxItemSize: 12,
    toolboxGap: 8,
};

const TreemapChart: React.FC<TreemapChartProps> = (
    {
        data,
        levelCount = 0,
        height = 1,
        width = 1,
        chartSettingsView = false,
        title = '',
        dataLabelPosition = DataLabelPosition.Off,
        description = '',
        datePeriod = '',
    },
) => {
    const chartRef = useRef<HTMLDivElement>(null);

    const showToolbox = !chartSettingsView && height !== 1;

    const treemapBounds = useMemo(() => {
        if (!showToolbox) {
            return {
                left: TREEMAP_CHART_PADDING.toolboxInset,
                right: TREEMAP_CHART_PADDING.toolboxInset,
                top: TREEMAP_CHART_PADDING.vertical,
                bottom: TREEMAP_CHART_PADDING.vertical,
            };
        }

        const rightWithToolbox = TREEMAP_CHART_PADDING.toolboxInset
            + TREEMAP_CHART_PADDING.toolboxItemSize
            + TREEMAP_CHART_PADDING.toolboxGap;

        return {
            left: TREEMAP_CHART_PADDING.horizontal,
            right: rightWithToolbox,
            top: TREEMAP_CHART_PADDING.vertical,
            bottom: TREEMAP_CHART_PADDING.vertical,
        };
    }, [showToolbox]);

    const hasHierarchy = useMemo(
        () => data.some((node) => 'children' in node && node.children.length > 0),
        [data],
    );

    const fontSize = useMemo(() => {
        if (chartSettingsView || width === 1) {
            return 11;
        }
        return 13;
    }, [chartSettingsView, width]);

    const treemapLevels = useMemo(() => {
        if (!hasHierarchy || levelCount === 0) {
            return undefined;
        }

        return buildTreemapLevels(levelCount, fontSize, dataLabelPosition);
    }, [hasHierarchy, levelCount, fontSize, dataLabelPosition]);

    const customTooltip = useCallback(
        (params: HierarchyTooltipParams) => formatHierarchyChartTooltip(params),
        [],
    );

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = echarts.init(chartRef.current);
        const handleResize = () => {
            if (!chartInstance.isDisposed()) {
                chartInstance.resize();
            }
        };

        const handleChatSidebarChange = () => {
            setTimeout(handleResize, 350);
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window && chartRef.current) {
            resizeObserver = new ResizeObserver(() => {
                handleResize();
            });
            resizeObserver.observe(chartRef.current);
        }

        window.addEventListener('resize', handleResize);
        window.addEventListener('chatSidebarOpened', handleChatSidebarChange);
        window.addEventListener('chatSidebarClosed', handleChatSidebarChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('chatSidebarOpened', handleChatSidebarChange);
            window.removeEventListener('chatSidebarClosed', handleChatSidebarChange);
            resizeObserver?.disconnect();
            if (!chartInstance.isDisposed()) {
                chartInstance.dispose();
            }
        };
    }, []);

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {
            return;
        }

        chartInstance.resize();
    }, [height, width]);

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {
            return;
        }

        chartInstance.setOption({
            textStyle: {
                fontFamily: '"Rubik", sans-serif',
                fontSize,
                fontWeight: 400,
            },
            tooltip: {
                trigger: 'item',
                formatter: customTooltip,
            },
            toolbox: {
                show: showToolbox,
                orient: 'vertical',
                right: TREEMAP_CHART_PADDING.toolboxInset,
                top: TREEMAP_CHART_PADDING.vertical,
                itemSize: TREEMAP_CHART_PADDING.toolboxItemSize,
                itemGap: 8,
                feature: {
                    ...buildChartImageDownloadToolboxFeatures({
                        title,
                        description,
                        datePeriod,
                        getChartInstance: () => (
                            chartRef.current ? echarts.getInstanceByDom(chartRef.current) : null
                        ),
                    }, showToolbox),
                },
            },
            series: [
                {
                    type: 'treemap',
                    ...treemapBounds,
                    data,
                    colorMappingBy: 'index',
                    roam: false,
                    nodeClick: false,
                    breadcrumb: {show: false},
                    itemStyle: TREEMAP_TILE_ITEM_STYLE,
                    upperLabel: {
                        show: hasHierarchy,
                        color: '#ffffff',
                        fontFamily: '"Rubik", sans-serif',
                        fontWeight: 500,
                        fontSize,
                        padding: [0, 6],
                        align: 'left',
                        verticalAlign: 'middle',
                        textBorderWidth: 0,
                        textShadowBlur: 0,
                        formatter: (params: Parameters<typeof treemapUpperLabelFormatter>[0]) =>
                            treemapUpperLabelFormatter(params, dataLabelPosition),
                        backgroundColor: TREEMAP_UPPER_LABEL_BACKGROUND,
                    },
            label: {
                show: true,
                color: '#ffffff',
                fontFamily: '"Rubik", sans-serif',
                fontWeight: 400,
                fontSize,
                lineHeight: fontSize + 2,
                textBorderWidth: 0,
                        textShadowBlur: 0,
                        formatter: (params: Parameters<typeof treemapTileLabelFormatter>[0]) =>
                            treemapTileLabelFormatter(params, dataLabelPosition),
                    },
                    levels: treemapLevels,
                },
            ],
        }, {notMerge: true});
    }, [data, showToolbox, treemapBounds, title, description, datePeriod, hasHierarchy, levelCount, treemapLevels, fontSize, dataLabelPosition, customTooltip]);

    return <div ref={chartRef} style={{width: '100%', height: '100%'}}/>;
};

export default TreemapChart;
